/* =========================================================================
   WCAG 2.2 SC 2.1.1 Keyboard / SC 2.1.2 No Keyboard Trap / SC 2.4.3 Focus
   Order / SC 4.1.2 Name-Role-Value — WS (LyveCom modal a11y).
   getbreathdeath.com remediation (case 514658-2026).

   The shoppable-video carousel is the LyveCom app block
   (shopify://apps/lyve-shoppable-video-stream). Clicking a tile opens a
   full-screen "stories" player that LyveCom renders into an OPEN SHADOW ROOT
   on a host appended to <body>:

       body > div.lyvecom-swiper-element > div.lyvecom-swiper-selector  (#shadow-root)

   Live manual pass (2026-06-19) found this player is keyboard-inaccessible
   and a keyboard trap:
     - the container has NO role / aria-modal / accessible name      -> 4.1.2, 2.4.3
     - every control (close X, play/pause, mute, like, share, Buy
       Now, prev/next) is a non-semantic <div>/<img> with no role,
       no tabindex and no accessible name                            -> 2.1.1, 4.1.2
     - there is NO keyboard way to close the player and focus is not
       trapped, so a keyboard/AT user is stuck while the background
       carousel stays operable behind it                             -> 2.1.2, 2.4.3

   Because the markup is vendor-injected (and inside a shadow root) the correct
   long-term surface is LyveCom; an escalation is filed and tracked in
   RemediationPostWork/VENDOR-LyveCom-VideoModal.md. Until LyveCom ships a
   native fix this shim layers the missing semantics + focus management on top.
   It is the shadow-DOM sibling of wcag-klaviyo-dialog.js (WS8.1).

   This wrapper, in vanilla JS, watches <body> for the player and:
     - records the user's focused element (the carousel tile) as the return
       target before moving focus into the dialog;
     - sets role="dialog" + aria-modal="true" + an accessible name on the host;
     - gives each vendor control role="button", tabindex="0", an aria-label,
       and Enter/Space activation (re-applied as the swiper changes slides);
     - moves focus to the close control on open;
     - traps Tab / Shift+Tab inside the (shadow) player;
     - closes on Escape by invoking LyveCom's OWN close control (the last <img>
       in the active slide's .actions_bar .wrapper);
     - on dismissal (player host removed from <body> by any path) returns focus
       to the recorded carousel tile.

   It also makes the CAROUSEL TILES keyboard-operable: LyveCom renders each tile
   as a div[role="button"] with only a click handler, so Enter/Space did not open
   the player (SC 2.1.1). enhanceControl() adds the missing Enter/Space activation
   (the carousel arrows are real <button>s and already work).

   It NEVER intercepts playback or commerce actions and only ever invokes
   LyveCom's own controls, so the vendor player's behavior is untouched.

   Fully defensive: every step is wrapped / guarded and no-ops if the vendor
   markup changes. Idempotent: each control is flagged once (data-wcag-lyve);
   the global observer is installed once. A double include or re-run is a no-op.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__wcagLyvecomModalInit) {
    return;
  }
  window.__wcagLyvecomModalInit = true;

  var WRAPPER_SEL = '.lyvecom-swiper-element'; // light-DOM wrapper added to <body>
  var HOST_SEL = '.lyvecom-swiper-selector'; // shadow host inside the wrapper
  var CAROUSEL_SEL = '.lyvecom_carousel-component'; // shadow host of the grid of tiles
  var TILE_SEL = '.wrapper-play-btn'; // a video tile that opens the player

  /* Vendor control selectors -> accessible name. The close + play/pause live as
     the two <img> in each slide's .actions_bar .wrapper (handled separately,
     first = play/pause, last = close). These are the standalone controls. */
  var CONTROLS = [
    { sel: '.mute_btn', label: 'Mute or unmute video' },
    { sel: '.share_btn', label: 'Share video' },
    { sel: '.like_btn', label: 'Like video' },
    { sel: '.add_btn', label: 'Add to cart' },
    { sel: '.swiper-button-prev', label: 'Previous video' },
    { sel: '.swiper-button-next', label: 'Next video' }
  ];

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), ' +
    '[tabindex]:not([tabindex="-1"]), [role="button"]:not([disabled])';

  var returnTarget = null; // element to restore focus to on dismiss
  var activeHost = null; // shadow host currently being managed
  var keydownHandler = null;
  var shadowObserver = null;
  var readyTimer = null;

  /* document.activeElement stops at the shadow host; descend into open shadow
     roots to find the element actually focused. */
  function deepActive() {
    var a = document.activeElement;
    while (a && a.shadowRoot && a.shadowRoot.activeElement) {
      a = a.shadowRoot.activeElement;
    }
    return a;
  }

  /* Is the element inside the player? (handles the host's shadow tree, which
     wrapper.contains() does not cross.) */
  function insideModal(el) {
    if (!el) {
      return false;
    }
    var wrapper = document.querySelector(WRAPPER_SEL);
    if (!wrapper) {
      return false;
    }
    var host = wrapper.querySelector(HOST_SEL);
    if (host && host.shadowRoot && el.getRootNode &&
        el.getRootNode() === host.shadowRoot) {
      return true;
    }
    return wrapper.contains(el);
  }

  /* Continuously remember the last element focused OUTSIDE the player (descending
     into shadow roots), so dismissal can return focus to the real trigger — the
     carousel tile lives in a shadow root, so e.target would retarget to the host.
     Recorded here rather than read at open time because focus can shift between
     the tile click and the player mounting. */
  document.addEventListener(
    'focusin',
    function () {
      var a = deepActive();
      if (!a || a === document.body || a === document.documentElement) {
        return;
      }
      if (insideModal(a)) {
        return;
      }
      returnTarget = a;
    },
    true
  );

  function visible(el) {
    if (!el || !el.getBoundingClientRect) {
      return false;
    }
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) {
      return false;
    }
    // off the active slide (swiper keeps other slides in the DOM, off-screen)
    if (r.bottom <= 0 || r.top >= window.innerHeight) {
      return false;
    }
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  function enhanceControl(el, label) {
    if (!el || el.getAttribute('data-wcag-lyve') === 'bound') {
      return;
    }
    el.setAttribute('data-wcag-lyve', 'bound');
    if (!el.getAttribute('role')) {
      el.setAttribute('role', 'button');
    }
    if (el.getAttribute('tabindex') === null) {
      el.setAttribute('tabindex', '0');
    }
    if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
      el.setAttribute('aria-label', label);
    }
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        try {
          el.click();
        } catch (err) {}
      }
    });
  }

  /* Add semantics to every control in the shadow root. Idempotent, so it is
     safe to re-run on each swiper slide change. */
  function enhanceControls(root) {
    if (!root) {
      return;
    }
    try {
      var wrappers = root.querySelectorAll('.actions_bar .wrapper');
      for (var i = 0; i < wrappers.length; i++) {
        var imgs = wrappers[i].querySelectorAll('img');
        if (imgs.length >= 1) {
          enhanceControl(imgs[0], 'Play or pause video');
        }
        if (imgs.length >= 2) {
          enhanceControl(imgs[imgs.length - 1], 'Close video');
        }
      }
      for (var c = 0; c < CONTROLS.length; c++) {
        var nodes = root.querySelectorAll(CONTROLS[c].sel);
        for (var n = 0; n < nodes.length; n++) {
          enhanceControl(nodes[n], CONTROLS[c].label);
        }
      }
    } catch (err) {}
  }

  /* The active slide's close control: last <img> in its .actions_bar .wrapper.
     Falls back to any visible close <img> if the active slide is not marked. */
  function closeControl(root) {
    if (!root) {
      return null;
    }
    var slide = root.querySelector('.swiper-slide-active') || root;
    var wrap = slide.querySelector('.actions_bar .wrapper');
    if (wrap) {
      var imgs = wrap.querySelectorAll('img');
      if (imgs.length) {
        return imgs[imgs.length - 1];
      }
    }
    // fallback: a bound close img that is currently visible
    var candidates = root.querySelectorAll('.actions_bar .wrapper img[data-wcag-lyve]');
    for (var i = 0; i < candidates.length; i++) {
      if (visible(candidates[i])) {
        return candidates[i];
      }
    }
    return null;
  }

  function focusables(root) {
    if (!root) {
      return [];
    }
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), visible);
  }

  function decorate(host) {
    if (host.getAttribute('role') !== 'dialog') {
      host.setAttribute('role', 'dialog');
    }
    host.setAttribute('aria-modal', 'true');
    if (!host.getAttribute('aria-label') && !host.getAttribute('aria-labelledby')) {
      host.setAttribute('aria-label', 'Shoppable video player');
    }
  }

  function moveFocusIn(root) {
    if (activeHost && activeHost.shadowRoot &&
        activeHost.shadowRoot.contains(deepActive())) {
      return; // focus already inside the player
    }
    var close = closeControl(root);
    if (close) {
      try {
        close.focus();
        return;
      } catch (e) {}
    }
    var f = focusables(root);
    if (f.length) {
      try {
        f[0].focus();
      } catch (e) {}
    }
  }

  function restoreFocus() {
    /* isConnected (not document.contains) so a shadow-DOM trigger still counts
       as a valid return target. */
    var t = returnTarget;
    if (!(t && t.isConnected)) {
      t =
        document.getElementById('MainContent') ||
        document.querySelector('main, [role="main"]');
      if (t && !t.hasAttribute('tabindex')) {
        t.setAttribute('tabindex', '-1');
      }
    }
    if (t) {
      try {
        t.focus();
      } catch (e) {}
    }
  }

  function teardown() {
    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler, true);
      keydownHandler = null;
    }
    if (shadowObserver) {
      shadowObserver.disconnect();
      shadowObserver = null;
    }
    if (readyTimer) {
      clearInterval(readyTimer);
      readyTimer = null;
    }
    activeHost = null;
  }

  function bindKeys(root) {
    keydownHandler = function (e) {
      if (!activeHost || !document.contains(activeHost)) {
        return;
      }
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        var close = closeControl(root);
        if (close) {
          try {
            close.click(); // invoke LyveCom's own close; removal handler restores focus
          } catch (err) {}
        }
        return;
      }
      if (e.key === 'Tab') {
        var f = focusables(root);
        if (!f.length) {
          return;
        }
        var first = f[0];
        var last = f[f.length - 1];
        var act = deepActive();
        var inside = root.contains(act);
        if (e.shiftKey) {
          if (act === first || !inside) {
            e.preventDefault();
            try {
              last.focus();
            } catch (err) {}
          }
        } else if (act === last || !inside) {
          e.preventDefault();
          try {
            first.focus();
          } catch (err) {}
        }
      }
    };
    document.addEventListener('keydown', keydownHandler, true);
  }

  /* The shadow host appears synchronously, but its shadow root is populated
     asynchronously. Poll briefly until the player chrome exists, then wire up. */
  function manage(host) {
    if (activeHost === host) {
      return;
    }
    teardown();
    activeHost = host;
    if (!returnTarget) {
      returnTarget = deepActive(); // fallback if focusin tracker has nothing yet
    }
    decorate(host);

    var tries = 0;
    readyTimer = window.setInterval(function () {
      tries++;
      var root = host.shadowRoot;
      var ready = root && root.querySelector('.actions_bar');
      if (ready) {
        clearInterval(readyTimer);
        readyTimer = null;
        enhanceControls(root);
        shadowObserver = new MutationObserver(function () {
          enhanceControls(root); // re-decorate controls when swiper swaps slides
        });
        shadowObserver.observe(root, { childList: true, subtree: true });
        bindKeys(root);
        moveFocusIn(root);
      } else if (tries > 40) {
        clearInterval(readyTimer);
        readyTimer = null;
      }
    }, 150);
  }

  function tick() {
    var wrapper = document.querySelector(WRAPPER_SEL);
    var host = wrapper ? wrapper.querySelector(HOST_SEL) : null;
    if (host) {
      manage(host);
    } else if (activeHost) {
      teardown();
      restoreFocus();
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    var raf =
      window.requestAnimationFrame ||
      function (cb) {
        return window.setTimeout(cb, 16);
      };
    raf(function () {
      scheduled = false;
      tick();
    });
  }

  /* ---- carousel tiles -------------------------------------------------------
     The carousel (a separate shadow host, .lyvecom_carousel-component) renders
     each video tile as a div[role="button"][tabindex="0"] with ONLY a click
     handler. A non-native element does not get Enter/Space activation for free,
     so a keyboard user can focus a tile but cannot open the player (SC 2.1.1).
     The carousel arrows are real <button>s and already work. enhanceControl adds
     the missing Enter/Space -> click (role/tabindex/label are already present, so
     those calls no-op). Tiles hydrate lazily, so each carousel's shadow root is
     observed and re-enhanced. */
  function enhanceTiles(root) {
    if (!root) {
      return;
    }
    try {
      var tiles = root.querySelectorAll(TILE_SEL);
      for (var i = 0; i < tiles.length; i++) {
        enhanceControl(tiles[i], 'Play video');
      }
    } catch (e) {}
  }

  function bindCarousel(host) {
    if (host.__wcagCarouselBound || !host.shadowRoot) {
      return;
    }
    host.__wcagCarouselBound = true;
    var root = host.shadowRoot;
    enhanceTiles(root);
    var obs = new MutationObserver(function () {
      enhanceTiles(root);
    });
    obs.observe(root, { childList: true, subtree: true });
  }

  function setupCarousel() {
    var tries = 0;
    var iv = window.setInterval(function () {
      tries++;
      var hosts = document.querySelectorAll(CAROUSEL_SEL);
      var pending = false;
      for (var i = 0; i < hosts.length; i++) {
        bindCarousel(hosts[i]);
        if (!hosts[i].__wcagCarouselBound) {
          pending = true;
        }
      }
      if ((hosts.length && !pending) || tries > 40) {
        clearInterval(iv);
      }
    }, 250);
  }

  function start() {
    var obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true });
    tick(); // handle a player already present at load
    setupCarousel(); // make the carousel tiles keyboard-operable
  }

  if (document.readyState !== 'loading') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
